<script setup lang="ts">
import { computed } from 'vue'
import type { MediaFileReference, Resource } from '../../../types/_general'
import { useQuery } from '../../../composables/api'

import mdiOpenInNew from '~icons/mdi/open-in-new'
import BDialog from '@/components/base/BDialog/BDialog.vue'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'

const props = defineProps<{
  resource?: Resource
  resourceId?: string
  file: MediaFileReference | File
}>()

const dialogOpen = defineModel<boolean>({ default: false })

const normalizedFile = computed(() => {
  if (props.file instanceof File) {
    return props.file
  } else {
    return {
      name: props.file.filename ?? '',
      type: props.file.content_type ?? '',
      size: props.file.size ?? 0,
    }
  }
})

const mediaFileQuery = useQuery(
  '/media--url/{resource}/{resource_id}/{bucket_id}',
  computed(() => ({
    path: {
      resource: props.resource ?? '',
      resource_id: props.resourceId ?? '',
      bucket_id: 'id' in props.file ? (props.file.id ?? '') : '',
    },
  })),
  {
    enabled: computed(
      () => !(props.file instanceof File) && props.file.id != null && props.resource != null && props.resourceId != null
    ),
  }
)

const href = computed(() => {
  if (props.file instanceof File) {
    return URL.createObjectURL(props.file)
  } else {
    return mediaFileQuery.data.value?.url
  }
})
</script>

<template>
  <slot
    name="activator"
    :open-dialog="() => (dialogOpen = true)"
    :href="href"
  ></slot>

  <b-dialog
    v-model="dialogOpen"
    height="90dvh"
    size="xlarge"
    fullscreen-action
  >
    <template #title>{{ normalizedFile.name ?? '' }}</template>

    <template #header-actions>
      <b-tooltip-btn
        :icon="mdiOpenInNew"
        tooltip="open_in_new_tab"
        :href="href"
        target="_blank"
        variant="text"
        density="compact"
      />
    </template>

    <div class="h-100 w-100 overflow-hidden position-relative">
      <v-progress-circular
        v-if="mediaFileQuery.isLoading.value"
        class="mx-auto"
        color="primary"
        size="3rem"
        indeterminate
      />

      <template v-else-if="href">
        <v-img
          v-if="normalizedFile.type.startsWith('image/')"
          :src="href"
          :alt="normalizedFile.name"
          height="100%"
          max-height="100%"
          width="100%"
          max-width="100%"
        />
        <v-video
          v-else-if="normalizedFile.type.startsWith('video/')"
          :src="href"
          height="100%"
          width="100%"
          autoplay
        />
        <object
          v-else-if="normalizedFile.type === 'application/pdf'"
          :key="href"
          class="w-100 h-100 overflow-hidden"
          :data="href"
          :type="normalizedFile.type"
        />
        <iframe
          v-else
          class="w-100 h-100 overflow-hidden"
          :src="href"
          sandbox=""
        />
      </template>
    </div>
  </b-dialog>
</template>

<style scoped>
object,
iframe {
  object-fit: contain;
  border: none;
}
</style>
